/**
 * Angle handling for the sketch geometry engine.
 *
 * TWO REPRESENTATIONS EXIST ON PURPOSE — see ADR 0004.
 *
 *   INTERNAL (0–359°)   Used for trig. `Math.cos`/`Math.sin` want a natural
 *                       range, and the canvas draws in this space.
 *   CABINET VISION      What is STORED and EXPOSED: 0, 45, 90, 135, -180,
 *   (signed, 8 values)  -135, -90, -45. Never +180, never 225/270/315.
 *
 * `sketch.walls.angle_deg` has a CHECK constraint permitting only the Cabinet
 * Vision set, so an out-of-convention value cannot reach the database — the
 * conversion below is the only sanctioned way across that boundary. Convert at
 * the edge; never let internal 0–359 values escape into stored data.
 *
 * Ported from reference/bluetooth-room-sketch-prototype/index.html.
 */

/** Screen space: y grows downward, so angles run clockwise from east. */
export function degToRad(deg) {
  return (deg * Math.PI) / 180;
}

/** Normalizes to [0, 360) — the internal representation. */
export function radToDeg(rad) {
  return ((rad * 180) / Math.PI + 360) % 360;
}

/**
 * Snaps an internal angle to the nearest allowed increment, staying in
 * [0, 360).
 *
 * `incrementDegrees` is a parameter rather than a hardcoded 45 because it is
 * the `snap_increment_degrees` business rule — CLAUDE.md requires configurable
 * behavior to be data. Callers pass the rule value; the default only keeps
 * pure-geometry tests readable.
 */
export function snapAngle(deg, incrementDegrees = 45) {
  if (!Number.isFinite(deg)) throw new TypeError(`snapAngle: expected a finite angle, received ${deg}`);
  if (!(incrementDegrees > 0)) throw new RangeError(`snapAngle: increment must be > 0, received ${incrementDegrees}`);
  return (Math.round(deg / incrementDegrees) * incrementDegrees) % 360;
}

/** The eight legal stored values, in the order Postgres's CHECK lists them. */
export const CV_ANGLES = Object.freeze([0, 45, 90, 135, -180, -135, -90, -45]);

/**
 * Internal (0–359) → Cabinet Vision signed convention.
 *
 * 225 → -135, 270 → -90, 315 → -45, and 180 → -180 (CV shows -180, never +180;
 * that asymmetry is the whole reason this cannot be a plain modulo).
 */
export function toCvAngle(deg) {
  let a = (Math.round(deg / 45) * 45) % 360;
  if (a > 180) a -= 360;
  if (a === 180) a = -180;
  return a === 0 ? 0 : a; // normalize -0 to 0 so equality checks behave
}

/**
 * Cabinet Vision signed convention → internal (0–359), for trig.
 *
 * The inverse of `toCvAngle`. Needed whenever stored geometry is loaded back
 * for rendering or re-solving.
 */
export function fromCvAngle(cvDeg) {
  if (!isCvAngle(cvDeg)) {
    throw new RangeError(
      `fromCvAngle: ${cvDeg} is not a Cabinet Vision angle. Legal values: ${CV_ANGLES.join(', ')}`,
    );
  }
  return ((cvDeg % 360) + 360) % 360;
}

/** True when a value is one of the eight storable angles. */
export function isCvAngle(value) {
  return CV_ANGLES.includes(value);
}

/**
 * Smallest absolute separation between two internal angles, in [0, 180].
 * Used to decide whether a stroke changed direction enough to be a corner.
 */
export function angleDelta(a, b) {
  return Math.abs((((a - b + 540) % 360)) - 180);
}

/** Unit direction vector for an internal angle. */
export function directionOf(internalDeg) {
  return { x: Math.cos(degToRad(internalDeg)), y: Math.sin(degToRad(internalDeg)) };
}
